import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';

export interface MentionUser {
  _id:          string;
  displayName:  string;
  userName:     string;
  profileImage: { urlSmall?: string; urlMedium?: string } | null;
  isFriend:     boolean;
}

@Directive({
  selector:   '[appMentionInput]',
  standalone: true,
  exportAs:   'appMentionInput',
})
export class MentionInputDirective {

  /** When true (default), plain Enter fires `mentionEnter` instead of a newline —
   *  used by chat-style comment/reply boxes to submit on Enter (Shift+Enter for a
   *  newline). Set to false for free-form multi-line fields (e.g. a post caption)
   *  where both Enter and Shift+Enter should just insert a line break. */
  @Input() submitOnEnter = true;

  @Output() mentionSearch   = new EventEmitter<string>();
  @Output() mentionDropdown = new EventEmitter<boolean>();
  @Output() valueChange     = new EventEmitter<string>();

  activeMentionStart = -1;

  constructor(private el: ElementRef<HTMLElement>) {}

  private get textInputEl(): HTMLInputElement | HTMLTextAreaElement | null {
    const native = this.el.nativeElement;
    return native instanceof HTMLInputElement || native instanceof HTMLTextAreaElement
      ? native
      : null;
  }

  // ── Plain text helpers ────────────────────────────────────────────────────

  getText(): string {
    const input = this.textInputEl;
    if (input) return input.value ?? '';
    return this.extractPlainText(this.el.nativeElement);
  }

  /**
   * Walk child nodes manually so that mention chips (@username spans)
   * contribute their text content and everything else contributes raw text.
   * This gives a clean plain-text string regardless of how many chips exist.
   */
  private extractPlainText(root: HTMLElement): string {
    let text = '';
    root.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent ?? '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        if (el.tagName === 'BR') {
          text += '\n';
        } else if (el.classList.contains('mention-chip')) {
          text += el.textContent ?? '';
        } else {
          text += this.extractPlainText(el);
        }
      }
    });
    return text;
  }

  setText(value: string): void {
    if (this.getText() !== value) {
      const input = this.textInputEl;
      if (input) {
        input.value = value;
      } else {
        const host = this.el.nativeElement;
        host.innerHTML = '';
        if (value) host.appendChild(document.createTextNode(value));
      }
    }
  }

  private getCaretOffset(): number {
    const input = this.textInputEl;
    if (input) {
      return input.selectionStart ?? input.value.length;
    }
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return this.getText().length;
    const range    = sel.getRangeAt(0);
    const preRange = range.cloneRange();
    preRange.selectNodeContents(this.el.nativeElement);
    preRange.setEnd(range.endContainer, range.endOffset);
    return preRange.toString().length;
  }

  // ── Listeners ─────────────────────────────────────────────────────────────

  @HostListener('input')
  onInput(): void {
    this.valueChange.emit(this.getText());

    const text  = this.getText();
    const caret = this.getCaretOffset();

    let mentionStart = -1;
    for (let i = caret - 1; i >= 0; i--) {
      const ch = text[i];
      if (ch === '@') { mentionStart = i; break; }
      if (/\s/.test(ch)) break;
    }

    if (mentionStart === -1) {
      if (this.activeMentionStart !== -1) {
        this.activeMentionStart = -1;
        this.mentionDropdown.emit(false);
      }
      return;
    }

    const partial = text.slice(mentionStart + 1, caret);

    if (/[^\w.]/.test(partial)) {
      this.activeMentionStart = -1;
      this.mentionDropdown.emit(false);
      return;
    }

    this.activeMentionStart = mentionStart;
    this.mentionDropdown.emit(true);
    this.mentionSearch.emit(partial);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      if (this.submitOnEnter && !e.shiftKey) {
        e.preventDefault();
        this.el.nativeElement.dispatchEvent(
          new CustomEvent('mentionEnter', { bubbles: true }),
        );
        return;
      }
      // Free-form mode: force a <br> for plain Enter too, so it matches
      // Shift+Enter's default browser behavior exactly (contenteditable's
      // native Enter handling inserts a new block element instead, which
      // extractPlainText() doesn't treat as a line break).
      if (!this.submitOnEnter && !e.shiftKey && !this.textInputEl) {
        e.preventDefault();
        this.insertLineBreak();
        return;
      }
    }
    if (e.key === 'Escape' && this.activeMentionStart !== -1) {
      this.activeMentionStart = -1;
      this.mentionDropdown.emit(false);
    }
  }

  private insertLineBreak(): void {
    // Delegate to the browser's own native line-break command — the same one
    // Shift+Enter uses by default — so plain Enter gets identical, correctly
    // rendered behavior on the very first press instead of a hand-rolled
    // <br> insertion (which needed a second press before the break showed up).
    // execCommand fires its own native 'input' event, so onInput() runs automatically.
    document.execCommand('insertLineBreak');
  }

  @HostListener('paste', ['$event'])
  onPaste(e: ClipboardEvent): void {
    e.preventDefault();
    const text  = e.clipboardData?.getData('text/plain') ?? '';
    const input = this.textInputEl;
    if (input) {
      const start = input.selectionStart ?? input.value.length;
      const end   = input.selectionEnd   ?? start;
      input.setRangeText(text, start, end, 'end');
      input.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }
    document.execCommand('insertText', false, text);
  }

  // ── Insert mention chip ───────────────────────────────────────────────────

  insertMention(user: MentionUser, _currentValue: string): string {
    if (this.activeMentionStart === -1) return this.getText();

    const caret = this.getCaretOffset();
    this.replaceRangeWithChip(this.activeMentionStart, caret, user);

    this.activeMentionStart = -1;
    this.mentionDropdown.emit(false);

    const newText = this.getText();
    this.valueChange.emit(newText);
    return newText;
  }

  // ── DOM helpers ───────────────────────────────────────────────────────────

  private replaceRangeWithChip(
    startOffset: number,
    endOffset:   number,
    user:        MentionUser,
  ): void {
    const input = this.textInputEl;
    if (input) {
      const before = input.value.slice(0, startOffset);
      const after  = input.value.slice(endOffset);
      const insert = `@${user.userName} `;
      input.value  = `${before}${insert}${after}`;
      const caret  = before.length + insert.length;
      input.setSelectionRange(caret, caret);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }

    const host  = this.el.nativeElement;
    const range = this.plainTextOffsetToRange(host, startOffset, endOffset);
    if (!range) return;

    range.deleteContents();

    const chip               = document.createElement('span');
    chip.contentEditable     = 'false';
    chip.className           = 'mention-chip';
    chip.dataset['userId']   = user._id;
    chip.dataset['userName'] = user.userName;
    chip.textContent         = `@${user.userName}`;

    const space = document.createTextNode('\u00A0');
    range.insertNode(space);
    range.insertNode(chip);

    const newRange = document.createRange();
    newRange.setStartAfter(space);
    newRange.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(newRange);

    host.dispatchEvent(new Event('input', { bubbles: true }));
  }

  private plainTextOffsetToRange(
    root:        HTMLElement,
    startOffset: number,
    endOffset:   number,
  ): Range | null {
    let charCount        = 0;
    let startNode: Node | null = null;
    let startNodeOff     = 0;
    let endNode:   Node | null = null;
    let endNodeOff       = 0;

    const walk = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node: Node | null;

    while ((node = walk.nextNode())) {
      const len = (node.textContent ?? '').length;

      if (!startNode && charCount + len >= startOffset) {
        startNode    = node;
        startNodeOff = startOffset - charCount;
      }
      if (!endNode && charCount + len >= endOffset) {
        endNode    = node;
        endNodeOff = endOffset - charCount;
        break;
      }
      charCount += len;
    }

    if (!startNode || !endNode) return null;

    const range = document.createRange();
    range.setStart(startNode, startNodeOff);
    range.setEnd(endNode, endNodeOff);
    return range;
  }
}